/**
 * Substitute tutor booking experiment (painted door).
 *
 * Problem this measures: a course has exactly one primary tutor, so when a
 * student wants a lesson at a time that tutor has not opened, the request
 * simply drops off. Nothing records that the demand existed, and the lost
 * revenue is invisible.
 *
 * The bet is that letting a student book a billable lesson with a vetted
 * substitute recovers those requests, pushes primary tutors to open more slots
 * (they lose the lesson otherwise), and gives newly onboarded marketplace
 * tutors billable hours.
 *
 * There is no substitute-matching backend yet, so the test variant is a
 * painted door. It presents as a shipped feature — anonymous vetted tutors
 * with bookable-looking times — because a surface labelled "coming soon"
 * measures curiosity, while a surface that looks real measures how many
 * students would actually go through with the booking. That is the number
 * needed to justify building the matching service.
 *
 * The student is told the feature is still rolling out at the moment they
 * press Book, before anything is committed and while their primary tutor's
 * real slots are still on screen beside it. Nothing here books anything, and
 * no real tutor's name, photo or schedule is ever shown. The control keeps
 * today's book-a-lesson modal untouched.
 */

/** PostHog multivariate flag: 'control' | 'test'. */
export const SUBSTITUTE_TUTOR_BOOKING_FEATURE_FLAG = 'substitute_tutor_booking';

export const SUBSTITUTE_TUTOR_BOOKING_VARIANTS = {
  CONTROL: 'control',
  TEST: 'test',
} as const;

/**
 * The entry point only appears when the primary tutor is offering fewer than
 * this many slots on the selected date. Above it the student already has real
 * choice and a substitute is noise; at or below it the request is at risk of
 * dropping off, which is the moment worth measuring. Zero slots counts, so the
 * "no slots available" dead end is covered too.
 */
export const SUBSTITUTE_TUTOR_SLOT_THRESHOLD = 5;

/**
 * How many slots the preview aims to show.
 *
 * The student picks a time, not a person — the tutor is assigned afterwards
 * from whoever matches — so the list is deliberately flat and generous. Eight
 * reads as real day-wide coverage while still fitting the modal without
 * scrolling.
 */
export const SUBSTITUTE_TUTOR_PREVIEW_SLOT_COUNT = 8;

/**
 * Start hours (24h, in the student's local time) the preview draws from.
 *
 * Spans the whole teaching day so the list reads as "wider coverage than your
 * tutor", which is the proposition being tested. The pool is far larger than
 * SUBSTITUTE_TUTOR_PREVIEW_SLOT_COUNT because candidates get dropped for being
 * in the past, clashing with the primary tutor, or overlapping each other at a
 * two-hour lesson length.
 */
export const SUBSTITUTE_TUTOR_PREVIEW_START_HOURS = [
  7,
  8,
  9,
  10,
  11,
  12,
  13,
  14,
  15,
  16,
  17,
  18,
  19,
  20,
  21,
];

/**
 * How the substitute match is described to the student.
 *
 * These are match *criteria* the real feature would enforce, not claims about
 * a specific person — no tutor is named, pictured or scheduled here, because
 * the student is choosing a time and the tutor is assigned afterwards.
 */
export const SUBSTITUTE_TUTOR_PREVIEW_MATCH_CRITERIA = [
  'Same subject & level',
  'Same gender',
  'Equal or more experience',
  'Same hourly rate or lower',
];
